import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, FormattedNumber, injectIntl } from 'react-intl';
import { getRoute } from '../../../routes';
import UserContext from '../../../UserContext';
import { withRouter } from '../../../utils/withRouterRef';
import LoadingState from '../../feedback/LoadingState';
import { CheckCircle, Edit, Error, Lock, ManageSearch, ScheduleSend, Send } from '@mui/icons-material';
import { Autocomplete, Button, CircularProgress, IconButton, MenuItem, Paper, TextField, Tooltip } from '@mui/material';
import { deleteSiemConfig, loadSiemConfig, postSiemConfig, putSiemConfig } from '../../../data/Siem';
import Confirmation from '../../feedback/Confirmation';
import './SiemAdministration.css';

class SiemAdministration extends React.Component {

  static propTypes = {
    navBar: PropTypes.object,
    navigate: PropTypes.func,
    tenant: PropTypes.object,
    operatorView: PropTypes.bool
  };

  static contextType = UserContext;

  constructor (props) {
    super(props);

    this.state = {
      isLoading: true,
      showWizard: false,
      showDetails: false
    };
    this.handleInputChange = this.handleInputChange.bind(this);
    this.saveConfig = this.saveConfig.bind(this);
    this.deleteConfig = this.deleteConfig.bind(this);
    this.loadConfig = this.loadConfig.bind(this);
    this.route = getRoute('siem');
  }

  async componentDidMount () {
    this.loadConfig();
    this.setState({
      isOperator: this.context.user.groups.includes('Operator')
    });
    this.autoRefreshInterval = setInterval(() => {
      if (!window.document.hidden) {
        this.loadConfig(false);
      }
    }, 15000);
  }

  componentWillUnmount () {
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
    }
  }

  handleInputChange (event) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    const nameError = name + 'Error';
    const nameHelperText = name + 'HelperText';

    this.setState({
      [name]: value
    });
    if (value !== '') {
      this.setState({
        [nameError]: false,
        [nameHelperText]: ''
      });
    }
  }

  async loadConfig (checkState = true) {
    try {
      const siemConfig = await loadSiemConfig(this.props.tenant?.tenantId);
      if (siemConfig) {
        this.setState({ siemConfig, ...siemConfig });
      }
      if (checkState && (siemConfig.state === 'CREATING' || siemConfig.state === 'DELETING')) {
        setTimeout(this.loadConfig, 5000);
      }
    } catch (error) {
      this.setState({ siemConfig: undefined });
    } finally {
      this.setState({
        isLoading: false
      });
    }
  }

  async saveConfig () {
    const { type, url, apiKey, bucketArn, prefix, domainArn, indexName, subnetIds, securityGroupIds } = this.state;
    const config = {};
    try {
      this.setState({ saving: true });
      if (type === 'splunk') {
        config.url = url;
        config.apiKey = apiKey;
      } else if (type === 'S3') {
        config.bucketArn = bucketArn;
        config.prefix = prefix;
      } else if (type === 'opensearch') {
        config.domainArn = domainArn;
        config.indexName = indexName;
        config.subnetIds = subnetIds;
        config.securityGroupIds = securityGroupIds;
      }

      const siemConfig = this.state.siemConfig ? await putSiemConfig(this.props.tenant?.tenantId, type, config) : await postSiemConfig(this.props.tenant?.tenantId, type, config);
      if (siemConfig) {
        this.setState({ siemConfig });
      }
    } finally {
      this.setState({ saving: false });
      this.loadConfig();
    }
  }

  async deleteConfig () {
    this.setState({ showSecondaryOkButtonProgress: true });
    try {
      const response = await deleteSiemConfig(this.props.tenant?.tenantId);
      console.log(response);
    } finally {
      this.setState({ showSecondaryOkButtonProgress: false });
      this.loadConfig();
    }
  }

  render () {
    const { operatorView } = this.props;
    const { isLoading, showWizard, showDetails, type, siemConfig, isOperator } = this.state;

    if (isLoading) {
      return <div className="siem root">
        <LoadingState isLoading={this.state.isLoading}
          loadingIcon={this.route.icon}
          description={<FormattedMessage id='siem.loadingState.header' />}
        />
      </div>;
    }

    return (
      <div className='siem root'>

        <div className="main">
          {!showDetails &&
          <Paper>
              <h2>SIEM<div>Security Information and Event Management</div></h2>
              <div className="overview">
                <div>
                  <p><FormattedMessage id='siem.overview.description1' /></p>
                  <p><FormattedMessage id='siem.overview.description2' /></p>
                  <p><FormattedMessage id='siem.overview.description3' /></p>
                </div>
                <div>
                  <img src='/img/siem/undraw_data-thief_d66l.svg' alt="SIEM"/>
                </div>
              </div>
              {/* The event log is independent of a configured SIEM interface, so its link is always shown. */}
              <div className='actions'>
                {!siemConfig &&
                <Button variant='contained' onClick={() => this.setState({ showWizard: true })}><FormattedMessage id='siem.action.create' /></Button>
                }
                <Button variant='outlined' startIcon={<ManageSearch />} onClick={() => this.props.navigate(getRoute('siemEvents').to)}>
                  <FormattedMessage id='siem.events.show' />
                </Button>
              </div>
          </Paper>
          }
          {siemConfig &&
          <Paper style={{ marginTop: '1rem' }} className='customerSiem'>
            <h2><div><FormattedMessage id='siem.title.yourSiem' /></div></h2>
            <div className="item">
              <div className={'status ' + siemConfig.state}>
                {(siemConfig.state === 'CREATING' || siemConfig.state === 'DELETING') &&
                  <CircularProgress size={24} />
                }
                {siemConfig.state === 'ACTIVE' &&
                  <CheckCircle />
                }
                {(siemConfig.state === 'CREATING_FAILED' || siemConfig.state === 'DELETING_FAILED') &&
                  <Error />
                }

              </div>
              <div className='name'><FormattedMessage id={'siem.type.' + siemConfig.type} /></div>
              <div><IconButton onClick={() => this.setState({ showWizard: true })}><Edit/></IconButton></div>
              <div style={{ flexGrow: 2 }}></div>
              {siemConfig.statistics &&
              <div className='stats'>
                <div className="header"><FormattedMessage id='siem.stats.header' /></div>
                <div className="attemptsRow">
                  <Tooltip title={<FormattedMessage id='siem.stats.success' />}><div className='attempts success'><Send /><FormattedNumber value={siemConfig.statistics.delivered} /></div></Tooltip>
                  <Tooltip title={<FormattedMessage id='siem.stats.toDeliver' />}><div className='attempts toDeliver'><ScheduleSend /><FormattedNumber value={siemConfig.statistics.records - siemConfig.statistics.delivered} /></div></Tooltip>
                </div>
              </div>
              }
            </div>
          </Paper>
          }
        </div>

        <Confirmation
            id="Wizard"
            title={this.state.siemConfig ? <FormattedMessage id='siem.action.change' /> : <FormattedMessage id='siem.action.create' />}
            open={!!showWizard}
            onClose={() => this.setState({ showWizard: false })}
            onConfirmation={this.saveConfig}
            showOkButtonProgress={this.state.saving}
            okButtonProps={{ variant: 'contained' }}
            okButtonText={<FormattedMessage id='save' />}
            disableOkButton={operatorView || !this.state.type ||
              (this.state.type === 'splunk' && (!this.state.url || !this.state.apiKey)) ||
              (this.state.type === 'S3' && (!this.state.bucketArn || !this.state.prefix)) ||
              (this.state.type === 'opensearch' && (!this.state.domainArn || !this.state.indexName))
            }
            showSecondaryOkButton={!operatorView && this.state.siemConfig}
            onSecondaryConfirmation={this.deleteConfig}
            secondaryOkButtonText={<FormattedMessage id="delete"/>}
            showSecondaryOkButtonProgress={this.state.showSecondaryOkButtonProgress}
            secondaryOkButtonProps={{ variant: 'text', color: 'error' }}
            message={<>
              <TextField
                size='small'
                id="type"
                name="type"
                label={'SIEM Tool'}
                value={this.state.type}
                onChange={this.handleInputChange}
                spellCheck="false"
                autoFocus
                select
                fullWidth
                SelectProps={{
                  native: false,
                  multiple: false
                }}
                disabled={this.state.siemConfig}
              >
                  <MenuItem key='splunk' value='splunk'>
                    <FormattedMessage id="siem.type.splunk"/>
                  </MenuItem>
                  <MenuItem key='S3' value='S3'>
                    <FormattedMessage id="siem.type.S3"/>
                  </MenuItem>
                  {isOperator &&
                  <MenuItem key='opensearch' value='opensearch'>
                    <FormattedMessage id="siem.type.opensearch"/> <span className="operatorsOnly"><Lock /></span>
                  </MenuItem>
                  }
              </TextField>
              {type === 'splunk' &&
                <>
                  <TextField
                    size='small'
                    id="url"
                    name="url"
                    label={'URL'}
                    value={this.state.url}
                    onChange={this.handleInputChange}
                    spellCheck="false"
                    fullWidth
                  />
                  <TextField
                    size='small'
                    id="apiKey"
                    name="apiKey"
                    label={'ApiKey'}
                    value={this.state.apiKey}
                    onChange={this.handleInputChange}
                    spellCheck="false"
                    fullWidth
                  />
                </>
              }
              {type === 'S3' &&
                <>
                  <TextField
                    size='small'
                    id="bucketArn"
                    name="bucketArn"
                    label={'BucketArn'}
                    value={this.state.bucketArn}
                    onChange={this.handleInputChange}
                    spellCheck="false"
                    fullWidth
                  />
                  <TextField
                    size='small'
                    id="prefix"
                    name="prefix"
                    label={'Prefix'}
                    value={this.state.prefix}
                    onChange={this.handleInputChange}
                    spellCheck="false"
                    fullWidth
                  />
                </>
              }
              {type === 'opensearch' &&
                <>
                  <TextField
                    size='small'
                    id="domainArn"
                    name="domainArn"
                    label={'Domain Arn'}
                    value={this.state.domainArn}
                    onChange={this.handleInputChange}
                    spellCheck="false"
                    fullWidth
                  />
                  <TextField
                    size='small'
                    id="indexName"
                    name="indexName"
                    label={'Index Name'}
                    value={this.state.indexName}
                    onChange={this.handleInputChange}
                    spellCheck="false"
                    fullWidth
                  />
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={this.state.subnetIds}
                    onChange={(_, newValue) => {
                      this.setState({
                        subnetIds: newValue
                          .map((v) => v.trim())
                          .filter(Boolean)

                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Subnet Ids"
                        placeholder="Wert eingeben und Enter drücken"
                      />
                    )}
                  />
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={this.state.securityGroupIds}
                    onChange={(_, newValue) => {
                      this.setState({
                        securityGroupIds: newValue
                          .map((v) => v.trim())
                          .filter(Boolean)

                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="SecurityGroup Ids"
                        placeholder="Wert eingeben und Enter drücken"
                      />
                    )}
                  />
                </>
              }
            </>}
          />
      </div>
    );
  }

}

export default withRouter(injectIntl(SiemAdministration));
